/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import type {
  AirmetConfig,
  AirmetFromBackend,
  SigmetConfig,
  SigmetFromBackend,
} from '@opengeoweb/api';
import { useAuthenticationContext, useAuthQuery } from '@opengeoweb/api';
import type {
  AirmetFromFrontend,
  AviationProduct,
  ProductStatus,
  SigmetFromFrontend,
} from '@opengeoweb/sigmet-airmet';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import {
  getAirmetConfig,
  getAirmetList,
  getSigmetConfig,
  getSigmetList,
  postAirmet,
  postSigmet,
} from './api';
import { humanizeApiErrorMessage } from './errorHandling';
import { aviationQueryOptions } from './queryOptions';
import type { TimeFilter } from '../../components/FilterList/filter-utils';
import { getApiParams } from '../../components/FilterList/filter-utils';
import { warningsFormActions } from '../../store';
import type { WarningType } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';

interface TACData {
  data: string;
}
type TACResult = UseQueryResult<{ data: string }>;

const getAviationSnackbarKey = (status: ProductStatus): string => {
  switch (status) {
    case 'PUBLISHED':
      return 'aviation-publish-success';
    case 'CANCELLED':
      return 'aviation-cancel-success';
    case 'DISCARDED':
      return 'aviation-discard-success';
    default:
      return 'aviation-save-success';
  }
};

export const useAirmetList = (
  disabled = false,
  timeFilter?: TimeFilter,
): UseQueryResult<AirmetFromBackend[]> => {
  const apiParams = timeFilter ? getApiParams(timeFilter) : {};

  return useAuthQuery(
    ['airmet-list', JSON.stringify(apiParams)],
    async (auth) => getAirmetList(auth, apiParams),
    disabled,
    1000 * 60,
  );
};

export const useSigmetList = (
  disabled = false,
  timeFilter?: TimeFilter,
): UseQueryResult<SigmetFromBackend[]> => {
  const apiParams = timeFilter ? getApiParams(timeFilter) : {};

  return useAuthQuery(
    ['sigmet-list', JSON.stringify(apiParams)],
    async (auth) => getSigmetList(auth, apiParams),
    disabled,
    1000 * 60,
  );
};
export const useAviationWarnings = (): (
  | SigmetFromBackend
  | AirmetFromBackend
)[] => {
  const { data: airmets } = useAirmetList();
  const { data: sigmets } = useSigmetList();
  return (airmets ?? ([] as (SigmetFromBackend | AirmetFromBackend)[])).concat(
    sigmets ?? [],
  );
};
export const useProductConfig = (
  warningType?: WarningType,
  disabled = false,
): UseQueryResult<SigmetConfig | AirmetConfig> => {
  const airmetQuery = useAuthQuery(
    ['airmet-config'],
    getAirmetConfig,
    disabled,
  );
  const sigmetQuery = useAuthQuery(
    ['sigmet-config'],
    getSigmetConfig,
    disabled,
  );

  return warningType === 'airmet' ? airmetQuery : sigmetQuery;
};

export const useFetchSigmetTAC = (
  product: AviationProduct,
  enabled = true,
): TACResult => {
  const { auth } = useAuthenticationContext();
  return useQuery({
    ...aviationQueryOptions.getSigmetTAC(product, auth!),
    enabled,
  });
};

export const useFetchAirmetTAC = (
  product: AviationProduct,
  enabled = true,
): TACResult => {
  const { auth } = useAuthenticationContext();
  return useQuery({
    ...aviationQueryOptions.getAirmetTAC(product, auth!),
    enabled,
  });
};

export const useFetchSigmetCancelTAC = (
  product: AviationProduct,
  enabled = true,
): TACResult => {
  const { auth } = useAuthenticationContext();
  return useQuery({
    ...aviationQueryOptions.getSigmetCancelTAC(product, auth!),
    enabled,
  });
};

export const useFetchAirmetCancelTAC = (
  product: AviationProduct,
  enabled = true,
): TACResult => {
  const { auth } = useAuthenticationContext();
  return useQuery({
    ...aviationQueryOptions.getAirmetCancelTAC(product, auth!),
    enabled,
  });
};

interface UseTACApi {
  fetchSigmetTAC: (product: AviationProduct) => Promise<TACData>;
  fetchAirmetTAC: (product: AviationProduct) => Promise<TACData>;
  fetchSigmetCancelTAC: (product: AviationProduct) => Promise<TACData>;
  fetchAirmetCancelTAC: (product: AviationProduct) => Promise<TACData>;
}

export const useTACApi = (): UseTACApi => {
  const { auth } = useAuthenticationContext();
  const queryClient = new QueryClient();

  return {
    fetchSigmetTAC: async (product: AviationProduct): Promise<TACData> =>
      queryClient.fetchQuery(aviationQueryOptions.getSigmetTAC(product, auth!)),
    fetchAirmetTAC: async (product: AviationProduct): Promise<TACData> =>
      queryClient.fetchQuery(aviationQueryOptions.getAirmetTAC(product, auth!)),
    fetchSigmetCancelTAC: async (product: AviationProduct): Promise<TACData> =>
      queryClient.fetchQuery(
        aviationQueryOptions.getSigmetCancelTAC(product, auth!),
      ),
    fetchAirmetCancelTAC: async (product: AviationProduct): Promise<TACData> =>
      queryClient.fetchQuery(
        aviationQueryOptions.getAirmetCancelTAC(product, auth!),
      ),
  };
};

/**
 * Hook for posting a SIGMET, with feedback.
 *
 * @param snackBarMessage - Message to show on success.
 * @param onDialogClose - Optional callback to close a dialog.
 * @returns A mutation result for posting a SIGMET.
 */
export const usePostSigmet = (): UseMutationResult<
  SigmetFromBackend,
  Error,
  SigmetFromFrontend
> => {
  const { auth } = useAuthenticationContext();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t } = useWarningsTranslation();

  return useMutation({
    mutationFn: async (formData: SigmetFromFrontend) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      dispatch(warningsFormActions.removeFormError());
      return postSigmet(auth, formData);
    },
    onError: (error) => {
      if (error.cause === 409) {
        dispatch(
          warningsFormActions.setFormValues({
            formState: 'edit',
          }),
        );
        dispatch(
          warningsFormActions.setFormError({
            severity: 'warning',
            message: `${t('aviation-post-conflict-title', { warningType: 'SIGMET' })}\n${t('aviation-post-conflict-info')}`,
          }),
        );
        void queryClient.invalidateQueries({ queryKey: ['sigmet-list'] });
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ['sigmet'] });

      const formattedError = humanizeApiErrorMessage(error);
      dispatch(
        warningsFormActions.setFormError({
          message: `Failed to save SIGMET: ${formattedError}`,
        }),
      );
    },
    onSuccess: async (data, variables) => {
      const snackbarKey = getAviationSnackbarKey(variables.changeStatusTo);
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
          key: t(snackbarKey, { warningType: 'SIGMET' }),
        }),
      );
      await queryClient.invalidateQueries({ queryKey: ['sigmet-list'] });
    },
  });
};
/**
 * Hook for posting an AIRMET, with feedback.
 *
 * @param snackBarMessage - Message to show on success.
 * @param onDialogClose - Optional callback to close a dialog.
 * @returns A mutation result for posting an AIRMET.
 */
export const usePostAirmet = (): UseMutationResult<
  AirmetFromBackend,
  Error,
  AirmetFromFrontend
> => {
  const { auth } = useAuthenticationContext();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t } = useWarningsTranslation();

  return useMutation({
    mutationFn: async (formData: AirmetFromFrontend) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      dispatch(warningsFormActions.removeFormError());
      return postAirmet(auth, formData);
    },

    onError: (error) => {
      if (error.cause === 409) {
        dispatch(
          warningsFormActions.setFormValues({
            formState: 'edit',
          }),
        );
        dispatch(
          warningsFormActions.setFormError({
            severity: 'warning',
            message: `${t('aviation-post-conflict-title', { warningType: 'AIRMET' })}\n${t('aviation-post-conflict-info')}`,
          }),
        );
        void queryClient.invalidateQueries({ queryKey: ['airmet-list'] });
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ['airmet'] });

      const formattedError = humanizeApiErrorMessage(error);

      dispatch(
        warningsFormActions.setFormError({
          message: `Failed to save AIRMET: ${formattedError}`,
        }),
      );
    },

    onSuccess: async (data, variables) => {
      const snackbarKey = getAviationSnackbarKey(variables.changeStatusTo);

      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
          key: t(snackbarKey, { warningType: 'AIRMET' }),
        }),
      );

      await queryClient.invalidateQueries({ queryKey: ['airmet-list'] });
    },
  });
};
